import { INewsItem } from './NewsItem';
import { getNewsDisplayStatus, NewsDisplayStatus } from './newsDisplayStatus';

export enum NewsSortColumn {
    Title = 'title',
    Status = 'status',
    StartDate = 'start',
    EndDate = 'end',
    VisibleDate = 'visible',
    ReadBy = 'read'
}

export type SortDirection = 'asc' | 'desc';

export type NewsListFilters = {
    search: string;
    recipient: string;
    statuses: NewsDisplayStatus[];
    sort: NewsSortColumn;
    direction: SortDirection;
};

const allStatuses = [
    NewsDisplayStatus.Draft,
    NewsDisplayStatus.Scheduled,
    NewsDisplayStatus.Live,
    NewsDisplayStatus.Expired,
    NewsDisplayStatus.Archived
];

export const defaultStatuses = allStatuses.filter(
    (status) => status !== NewsDisplayStatus.Archived
);

export const defaultFilters: NewsListFilters = {
    search: '',
    recipient: '',
    statuses: defaultStatuses,
    sort: NewsSortColumn.VisibleDate,
    direction: 'desc'
};

const sortColumns = Object.keys(NewsSortColumn).map((key) => NewsSortColumn[key]);

export function filtersFromSearch(search: string): NewsListFilters {
    const params = new URLSearchParams(search);
    const status = params.get('status');
    const sort = params.get('sort') as NewsSortColumn;

    return {
        search: params.get('q') || '',
        recipient: params.get('recipient') || '',
        statuses:
            status === null
                ? defaultStatuses
                : (status
                      .split(',')
                      .filter(
                          (value) => allStatuses.indexOf(value as NewsDisplayStatus) !== -1
                      ) as NewsDisplayStatus[]),
        sort: sortColumns.indexOf(sort) !== -1 ? sort : defaultFilters.sort,
        direction: params.get('order') === 'asc' ? 'asc' : 'desc'
    };
}

export function filtersToSearch(filters: NewsListFilters): string {
    const params = new URLSearchParams();
    if (filters.search) {
        params.set('q', filters.search);
    }
    if (filters.recipient) {
        params.set('recipient', filters.recipient);
    }
    if (filters.statuses.join(',') !== defaultStatuses.join(',')) {
        params.set('status', filters.statuses.join(','));
    }
    if (filters.sort !== defaultFilters.sort || filters.direction !== defaultFilters.direction) {
        params.set('sort', filters.sort);
        params.set('order', filters.direction);
    }
    const search = params.toString();
    return search ? `?${search}` : '';
}

const columnsSortedAscendingFirst = [NewsSortColumn.Title, NewsSortColumn.Status];

export function nextSort(filters: NewsListFilters, column: NewsSortColumn): NewsListFilters {
    if (filters.sort === column) {
        return { ...filters, direction: filters.direction === 'asc' ? 'desc' : 'asc' };
    }
    const direction = columnsSortedAscendingFirst.indexOf(column) !== -1 ? 'asc' : 'desc';
    return { ...filters, sort: column, direction };
}

function dateText(value: string | null | undefined): string {
    return value ? value.substring(0, 10) : '';
}

function sortValue(item: INewsItem, column: NewsSortColumn, today: string): string | number {
    switch (column) {
        case NewsSortColumn.Title:
            return (item.title || '').toLocaleLowerCase();
        case NewsSortColumn.Status:
            return allStatuses.indexOf(getNewsDisplayStatus(item, today));
        case NewsSortColumn.StartDate:
            return dateText(item.startDate);
        case NewsSortColumn.EndDate:
            return dateText(item.endDate);
        case NewsSortColumn.ReadBy:
            return item.readCount || 0;
        default:
            return dateText(item.visibleDate);
    }
}

function matchesSearch(item: INewsItem, search: string): boolean {
    const query = search.toLowerCase();
    return (
        (item.title || '').toLowerCase().includes(query) ||
        (item.content || '').toLowerCase().includes(query) ||
        String(item.visibleDate || '')
            .toLowerCase()
            .includes(query)
    );
}

function putMissingValueLast(valueA: string | number, valueB: string | number): number {
    if (valueA === valueB) {
        return 0;
    }
    return valueA === '' ? 1 : -1;
}

export function filterAndSortNews(
    newsItems: INewsItem[],
    filters: NewsListFilters,
    today: string
): INewsItem[] {
    const filtered = newsItems.filter(
        (item) =>
            (filters.statuses.length === 0 ||
                filters.statuses.indexOf(getNewsDisplayStatus(item, today)) !== -1) &&
            matchesSearch(item, filters.search) &&
            (!filters.recipient ||
                (Array.isArray(item.recipients) &&
                    item.recipients.some((recipient) => recipient.role === filters.recipient)))
    );

    const direction = filters.direction === 'asc' ? 1 : -1;

    return filtered.sort((a, b) => {
        const valueA = sortValue(a, filters.sort, today);
        const valueB = sortValue(b, filters.sort, today);

        if (valueA === '' || valueB === '') {
            return putMissingValueLast(valueA, valueB);
        }
        if (valueA < valueB) {
            return -direction;
        }
        if (valueA > valueB) {
            return direction;
        }
        return b.id - a.id;
    });
}
